import type { Draft } from "immer";
import { ActiveTimeEntry, RawActiveTimeEntry, TimeEntryInfo } from "shared";
import { getFullName } from "utilz/helpers";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

interface TimeEntryState {
  activeTimerInstanceId: ReturnType<typeof setInterval> | null;
  isTimeEntryModalOpen: boolean;
  activeTimeEntriesDetails: Array<ActiveTimeEntry>;
  activeTimeEntries: Record<string, TimeEntryInfo>;
}

interface TimeEntryAction {
  toggleTimeEntryModalOpen: (value: boolean) => void;
  setActiveTimeEntries: (timeEntries: Array<RawActiveTimeEntry>) => void;
  addTimeEntry: (timeEntry: ActiveTimeEntry) => void;
  removeTimeEntry: ({
    serviceId,
    timeEntryId,
    serviceType,
  }: {
    timeEntryId: string;
    serviceId: string;
    serviceType: "ticket" | "request" | "project" | "task";
  }) => void;

  resetTimeEntryData: () => void;
}

export interface TimeEntryInterface extends TimeEntryState, TimeEntryAction {}

type TimeEntryStore = TimeEntryState & TimeEntryAction;
type ImmerSet = (fn: (state: Draft<TimeEntryStore>) => void) => void;
type ImmerGet = () => TimeEntryStore;

const initialTimeEntryData: TimeEntryState = {
  activeTimerInstanceId: null,
  isTimeEntryModalOpen: false,
  activeTimeEntriesDetails: [],
  activeTimeEntries: {},
};

export const timeEntrySlice = immer<TimeEntryStore>((set, get) => ({
  ...initialTimeEntryData,

  resetTimeEntryData() {
    set((state) => {
      const timerId = get().activeTimerInstanceId;
      if (timerId != null) {
        clearInterval(timerId);
      }
      state.activeTimerInstanceId = initialTimeEntryData.activeTimerInstanceId;
      state.isTimeEntryModalOpen = initialTimeEntryData.isTimeEntryModalOpen;
      state.activeTimeEntriesDetails =
        initialTimeEntryData.activeTimeEntriesDetails;
      state.activeTimeEntries = initialTimeEntryData.activeTimeEntries;
    });
  },

  toggleTimeEntryModalOpen(value) {
    set((state) => {
      state.isTimeEntryModalOpen = value;
    });
  },

  setActiveTimeEntries(timeEntries: Array<RawActiveTimeEntry>) {
    set((state) => {
      const timeEntryIds: Record<string, TimeEntryInfo> = {};
      const activeTimeEntriesDetails = timeEntries.map((item) => {
        timeEntryIds[item.service_id] = {
          mapping_id: item.mapping_id,
          service_id: item.service_id,
          service_type: item.service_type,
        };
        return {
          ...item,
          customer_user_name: getFullName(
            item?.customer_user_first_name || item?.first_name,
            item?.customer_user_middle_name || item?.middle_name,
            item?.customer_user_last_name || item?.last_name,
          ),
        };
      });
      state.activeTimeEntriesDetails = [...activeTimeEntriesDetails];
      state.activeTimeEntries = timeEntryIds;
      const currentTimerId = get().activeTimerInstanceId;
      if (currentTimerId != null) {
        clearInterval(currentTimerId);
        state.activeTimerInstanceId = null;
      }
      if (activeTimeEntriesDetails.length > 0) {
        startAllTimeEntryCounter(set, get);
      }
    });
  },
  addTimeEntry(timeEntry) {
    set((state) => {
      const timeEntries = [...state.activeTimeEntriesDetails, timeEntry];
      const index = state.activeTimeEntriesDetails.findIndex(
        (item) => item.mapping_id === timeEntry.mapping_id,
      );
      if (index === -1) {
        state.activeTimeEntriesDetails = timeEntries;
      }
      state.activeTimeEntries[timeEntry.service_id] = {
        mapping_id: timeEntry.mapping_id,
        service_id: timeEntry.service_id,
        service_type: timeEntry.service_type,
      };
    });
    // Kick off the 1s ticking counter if it isn't already running (e.g. this is
    // the first active timer, added via MQTT). Without this the entry sits at 0.
    if (get().activeTimerInstanceId == null) {
      startAllTimeEntryCounter(set, get);
    }
  },
  removeTimeEntry({ timeEntryId, serviceId, serviceType }) {
    set((state) => {
      const timeEntries = [...state.activeTimeEntriesDetails];
      const index = timeEntries.findIndex(
        (item) =>
          item.mapping_id === timeEntryId &&
          item.service_id === serviceId &&
          item.service_type === serviceType,
      );

      // Guard: index === -1 would make splice remove the last entry.
      if (index === -1) return;

      timeEntries.splice(index, 1);
      state.activeTimeEntriesDetails = timeEntries;
      delete state.activeTimeEntries[serviceId];
    });
  },
}));

const startAllTimeEntryCounter = (set: ImmerSet, get: ImmerGet) => {
  const interval = setInterval(() => {
    const timeEntries: Array<ActiveTimeEntry> = [
      ...Array.from(get().activeTimeEntriesDetails),
    ];
    const activeTimerId = get().activeTimerInstanceId;
    if (timeEntries.length <= 0 && activeTimerId !== null) {
      clearInterval(activeTimerId);
      set((state) => {
        state.activeTimerInstanceId = null;
      });
      return;
    }

    for (let i = 0; i < timeEntries.length; i++) {
      let timeEntry = timeEntries[i];
      const startTimeValue = timeEntry.start_time;
      if (startTimeValue != null) {
        const currentTime = new Date();
        const timeInSecond = Math.floor(
          (currentTime.getTime() - new Date(`${startTimeValue}`).getTime()) /
            1000,
        );
        timeEntry = {
          ...timeEntry,
          activeTimerValueInSeconds: timeInSecond,
        };
        timeEntries[i] = timeEntry;
      }
    }
    set((state) => {
      state.activeTimeEntriesDetails = [...timeEntries];
    });
  }, 1000);
  // Store the interval id synchronously so a concurrent add (within the first
  // tick) sees a running counter and doesn't start a second interval.
  set((state) => {
    state.activeTimerInstanceId = interval;
  });
};

export const useTimeEntryStore = create<TimeEntryInterface>()((...a) => ({
  ...timeEntrySlice(...a),
}));
